"use client";
import { usePostHog } from "posthog-js/react";

import { AuthLink } from "@/components/shared/auth-link";
import { Button } from "@/lib/components/button";
import { ShimmerButton } from "@/lib/components/shimmer-button";
import { useAppConfig } from "@/lib/config";

import { useI18n } from "@llmgateway/i18n/react";

import { AnimatedGroup } from "./animated-group";

export default function CallToAction() {
	const { t } = useI18n();
	const config = useAppConfig();
	const posthog = usePostHog();
	return (
		<section className="relative py-32 md:py-40 overflow-hidden">
			{/* Gradient separator at top */}
			<div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />

			{/* Atmospheric background */}
			<div className="absolute inset-0 bg-gradient-to-b from-background via-foreground/[0.02] to-background" />

			{/* Soft radial glow */}
			<div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-blue-500/[0.06] dark:bg-purple-500/[0.06] rounded-full blur-3xl pointer-events-none" />

			<div className="container relative mx-auto px-4">
				<div className="max-w-3xl mx-auto text-center">
					<AnimatedGroup preset="blur-slide" className="space-y-6">
						<h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
							{t("landing.cta.titleLine1")}
							<br />
							{t("landing.cta.titleLine2")}
						</h2>
						<p className="text-lg text-muted-foreground max-w-xl mx-auto">
							{t("landing.cta.description")}
						</p>
					</AnimatedGroup>

					<AnimatedGroup
						preset="blur-slide"
						className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
					>
						<AuthLink
							href="/signup"
							className="w-full sm:w-auto"
							onClick={() =>
								posthog.capture("cta_clicked", {
									location: "landing_bottom",
									cta: "create_free_account",
								})
							}
						>
							<ShimmerButton
								background="rgb(37, 99, 235)"
								className="shadow-2xl shadow-blue-500/25 px-8 py-4 text-base font-medium w-full sm:w-auto"
							>
								{t("landing.cta.createAccount")}
							</ShimmerButton>
						</AuthLink>
						<Button
							variant="outline"
							className="border-border bg-transparent text-foreground hover:bg-muted px-8 py-6 text-base w-full sm:w-auto"
							asChild
						>
							<a
								href={`${config.docsUrl}/self-host`}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() =>
									posthog.capture("cta_clicked", {
										location: "landing_bottom",
										cta: "self_host",
									})
								}
							>
								{t("landing.cta.selfHost")}
							</a>
						</Button>
					</AnimatedGroup>
				</div>
			</div>
		</section>
	);
}
